import { html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { WEB_COMPONENT_GENERIC_DASHBOARD_SETTINGS_PANEL } from '../../../webcomponents.constants';
import { styles } from '../view/styles';
import { GDSettingsPanelEvents } from '../types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import _, { cloneDeep } from 'lodash';
import { connect } from 'pwa-helpers';
import { RootState, store } from '../../../redux/store';
import {
  resetWidgets,
  selectFilteredWidgets,
  selectFilteredWidgetsSections,
  selectWidgetsSearchText,
  toggleIsOpenStateForWidgetCategory,
  updateSettingsPanelWidgetsActive2State,
  updateWidgetsSearchText,
} from '../../redux/dashboards.widgets.slice';
import { DashboardReduxWidget, UpdateWidgetActiveStatePayload } from '../../redux/types';
import { translateFn } from '../../../utils/translator.helper';
import { DashboardAppState, selectDashboardsAppState } from '../../redux/dashboards.app.reducer';
import { WidgetCategoryConfig } from '@nirvana/shared/dtos/types/generic-dashboards/gz-console';
import { getTranslatedWidgetTitle } from '../../utils/translations';
import { emphasizeString } from '../../../utils/htmlElement.utils';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

/**
 *  @summary Component that is used to control display/remove widgets to/from a generic dashboard's view
 *  @tag gz-dashboard-settings-panel
 */
@customElement(WEB_COMPONENT_GENERIC_DASHBOARD_SETTINGS_PANEL)
export class GzDashboardSettingsPanelWebComponent extends connect(store)(LitElement) {
  @property({ type: Boolean, attribute: true, reflect: true })
  hasSearchInput = false;

  @property({ type: String, attribute: true, reflect: true })
  searchInputPlaceholder = translateFn('Search');

  @property({ type: String, attribute: true, reflect: true })
  noWidgetsFoundMessage = translateFn('No widgets found');

  /**
   * @internal
   * @state
   */
  private widgets: Array<DashboardReduxWidget>;

  /**
   * @internal
   * @state
   */
  private widgetSections: Array<WidgetCategoryConfig>;

  /**
   * @internal
   */
  private searchModeActive = false;

  /**
   * @internal
   */
  private _widgetsCheckedInitialState: { [key: string]: { isActive: boolean; isSelected: boolean } } = {};
  /**
   * @internal
   */
  private _widgetsCheckedActualState: { [key: string]: { isActive: boolean; isSelected: boolean } } = {};
  /**
   * Used to inform the component it should render `noWidgetsFoundMessage`
   * @internal
   */
  private _shouldDisplayEmptyContent = false;

  /**
   * @internal
   */
  @query('#applyButton')
  private applyButton: GzButtonWebComponent;

  static get styles() {
    return styles;
  }

  connectedCallback() {
    super.connectedCallback();
  }

  public stateChanged(rootState: RootState) {
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    this.widgets = selectFilteredWidgets(dashboardsAppState);
    this.widgetSections = selectFilteredWidgetsSections(dashboardsAppState);
    this.searchModeActive = selectWidgetsSearchText(dashboardsAppState) !== '';

    this.createStates();

    this._shouldDisplayEmptyContent = this.widgets.length === 0;

    this.requestUpdate();
  }

  /**
   * Method used to create the component's internal states:
   * _widgetsCheckedInitialState - used to reflect the initial widgets checked status
   * _widgetsCheckedActualState - used to reflect the actual checked status at all times -> will be updated when using apply button
   * At first, the selected state of each widget is set to the same value as its active state
   * @private
   */
  private createStates() {
    for (const widget of this.widgets) {
      this._widgetsCheckedActualState[widget.id] =  {
        isActive: widget.isActive, // whether the widget is active or not
        isSelected: widget.isSelected, // whether the widget is selected in the settings panel or not
      };
      this._widgetsCheckedInitialState[widget.id] = {
        isActive: widget.isActive,
        isSelected: widget.isSelected,
      }
    }
  }

  render() {
    return html`
      <div id="dashboardSettingsPanel" class="dashboard-settings-panel d-flex flex-column">
        <div id="headerSection" class="header">
          <gz-button id="resetWidgetsButton" type="quiet" @click="${this.handleResetWidgetsClicked}"
            buttonfontsize="small">${translateFn('Reset Widgets')}</gz-button
          >
          <gz-title id="panelTitle" class="panel-title mt-2 mb-4"" fontSize="16">${translateFn('Settings')}</gz-title>
          ${this.renderSearchInputIfAvailable()}
        </div>
        <div id="contentSection" class="content mt-1">${this.renderAccordions()} ${this.displayEmptyContentIfNecessary()}</div>
        <div id="footerSection" class="footer pl-2 d-flex align-items-center">
          <gz-button id="applyButton" disabled="true" @click="${this.handleApplyButtonClicked}"
            >${translateFn('Apply')}</gz-button
          >
        </div>
      </div>
    `;
  }

  private renderAccordions() {
    return html`
      ${this.widgetSections.map((widgetsSection: WidgetCategoryConfig) => this.renderAccordion(widgetsSection))}
    `;
  }

  /**
   * Method used to create Search Input element depending on property `shouldDisplaySearchInput`
   * @private
   */
  private renderSearchInputIfAvailable() {
    return this.hasSearchInput
      ? html` <gz-input-search
          @change="${this.handleInputChange}"
          id="searchInput"
          placeholder="${this.searchInputPlaceholder}"
        ></gz-input-search>`
      : nothing;
  }

  /**
   * Method used to render the accordion elements
   * @param widgetCategory
   * @private
   */
  private renderAccordion(widgetCategory: WidgetCategoryConfig) {
    const sectionsWidgets: Array<DashboardReduxWidget> = this.widgets.filter(
      widget => widget.category === widgetCategory.category
    );

    return html`
      <gz-accordion
        isCompact
        accordionTitle="${widgetCategory.title}"
        ?isOpen="${widgetCategory.isOpen || this.searchModeActive}"
        ?nonCollapsable="${this.searchModeActive}"
        @open="${() => this.handleAccordionToggle(widgetCategory, true)}"
        @close="${() => this.handleAccordionToggle(widgetCategory, false)}"
        >${sectionsWidgets.map((widget: DashboardReduxWidget) => this.renderWidget(widget))}
      </gz-accordion>
    `;
  }

  /**
   * Method used to create widget elements using
   * @param widget
   * @private
   */
  private renderWidget(widget: DashboardReduxWidget) {
    const decoratedLabel = widget.decoratedTextOnSearch
      ? widget.decoratedTextOnSearch
      : getTranslatedWidgetTitle(widget.id);

    return html`
      <gz-list-item>
        <div class="d-flex flex-row">
          <gz-checkbox
            ?disabled="${!widget.config.closable}"
            ?checked="${this._widgetsCheckedActualState[widget.id].isSelected}"
            value="${widget.id}"
            @change="${this.handleCheckboxChecked}"
            ><gz-text class="widget-title" fontSize="14">${unsafeHTML(decoratedLabel)}</gz-text></gz-checkbox
          >
        </div>
      </gz-list-item>
    `;
  }

  /**
   * Method used to display a message (stored in `noWidgetsFoundMessage` property) informing the user his search (using
   * the Search Input if available) came up empty
   * @private
   */
  private displayEmptyContentIfNecessary(): TemplateResult {
    return this._shouldDisplayEmptyContent
      ? html`<gz-text fontSize="14" class="pl-2 mt-2">${this.noWidgetsFoundMessage}</gz-text>`
      : html``;
  }

  private handleAccordionToggle(category: WidgetCategoryConfig, opened: boolean) {
    store.dispatch(toggleIsOpenStateForWidgetCategory({ widgetCategory: category, isOpen: opened }));
  }

  private handleCheckboxChecked(event: CustomEvent): void {
    this.updateStates(event.detail.value, event.detail.checked);
    this.enableApplyButtonIfNecessary();
  }

  private handleInputChange(event) {
    const inputText = event.detail.sourceEvent.trim().toLowerCase();
    store.dispatch(updateWidgetsSearchText(inputText));
    this.filterSearch(inputText);
  }

  filterSearch(filter: string): void {
    const filtered = _.cloneDeep(this.widgets);

    filtered.forEach(widget => {
      widget.decoratedTextOnSearch = emphasizeString(filter, getTranslatedWidgetTitle(widget.id));
    });

    this.widgets = filtered;
  }

  private handleApplyButtonClicked(event) {
    const payloads: Array<UpdateWidgetActiveStatePayload> = [];

    for (const widgetId of Object.keys(this._widgetsCheckedActualState)) {
      if (this._widgetsCheckedInitialState[widgetId].isActive !== this._widgetsCheckedActualState[widgetId].isSelected) {
        payloads.push({
          widgetId: widgetId,
          isSelected: this._widgetsCheckedActualState[widgetId].isSelected, // keep state
          isActive: this._widgetsCheckedActualState[widgetId].isSelected,
        });
      }
    }

    this._widgetsCheckedInitialState = cloneDeep(this._widgetsCheckedActualState);

    store.dispatch(updateSettingsPanelWidgetsActive2State(payloads));

    this.dispatchEvent(
      new CustomEvent(GDSettingsPanelEvents.APPLY_BUTTON_CLICKED, {
        bubbles: true,
      })
    );

    const button = event.target as GzButtonWebComponent;
    button.disabled = true;
  }

  private handleResetWidgetsClicked() {
    store.dispatch(resetWidgets());
    this.dispatchEvent(
      new CustomEvent(GDSettingsPanelEvents.RESET_WIDGETS_BUTTON_CLICKED, {
        bubbles: true,
      })
    );
  }

  /**
   * Method used to update states every time the user clicks a widget checkbox
   * @private
   */
  private updateStates(widgetValue: string, checked: boolean): void {
    this._widgetsCheckedActualState[widgetValue].isSelected = checked;

    const payloads: Array<UpdateWidgetActiveStatePayload> = [];

    for (const widgetId of Object.keys(this._widgetsCheckedActualState)) {
      if (this._widgetsCheckedInitialState[widgetId].isSelected !== this._widgetsCheckedActualState[widgetId].isSelected) {
        payloads.push({
          widgetId: widgetId,
          isActive: this._widgetsCheckedActualState[widgetId].isActive, // keep state
          isSelected: this._widgetsCheckedActualState[widgetId].isSelected,
        });
      }
    }
    store.dispatch(updateSettingsPanelWidgetsActive2State(payloads));
  }

  /**
   * Method used to enable 'Apply' button if widgetsCheckedInitialState !== widgetsCheckedActualState
   * @private
   */
  private enableApplyButtonIfNecessary(): void {
    const stateKeys = Object.keys(this._widgetsCheckedInitialState);

    for (const key of stateKeys) {
      if (this._widgetsCheckedInitialState[key].isActive !== this._widgetsCheckedActualState[key].isSelected) {
        this.applyButton.disabled = false;
        return;
      }
    }

    this.applyButton.disabled = true;
  }
}
